import { z } from 'zod';
import { siteResourceViewSchema, type SiteResourceView } from '../../types/siteResourceEvidence';

/** A documented campus analogy, never a physical capacity solver.
 * Call only on the actual current, source-validated SED view during generation;
 * freeze its full input/result before the report source hash. This function does
 * not authorize input provenance, report usability, Intent or CFS adoption.
 */
export const CAMPUS_RESOURCE_SCREENING_VERSION = 'campus-resource-analogy-v1' as const;
export const CAMPUS_REFERENCE_CATALOG_VERSION = '2026-10-02-vantage-campus-cases-v1' as const;

export const CAMPUS_REFERENCE_CASES = Object.freeze([
  Object.freeze({
    id: 'vantage-quincy-wa1', campusAcres: 68, criticalITMW: 89,
    scope: 'Operator-published whole-campus critical IT specification; Washington campus',
    sourceUrl: 'https://vantage-dc.com/data-center-locations/north-america/quincy-washington/',
  }),
  Object.freeze({
    id: 'vantage-phoenix-az1', campusAcres: 50, criticalITMW: 176,
    scope: 'Operator-published planned full-build-out whole-campus critical IT specification; Arizona campus',
    sourceUrl: 'https://vantage-dc.com/data-center-locations/north-america/phoenix-arizona/',
  }),
] as const);

/** These are proposed screening hypotheses, not sourced site facts or standards.
 * No owner load, economic what-if or selected generation result is an input.
 */
export interface CampusScreeningAssumptions {
  incrementalReservedLandFraction: number;
  peakFacilityToITRatio: number;
  roundDownITMW: number;
  /** A proposed upper budget condition, not predicted water consumption. */
  normalPeakNonEvaporativeWithdrawalBudgetGpd: number;
  /** Budget withheld from modeled routine operations; not a fire-water design. */
  protectedWaterBudgetGpd: number;
  assumedDesignDryBulbC: number;
  /** Required gas energy at site-net peak output; HHV, not available gas flow. */
  assumedGasHeatRateHHVMMBtuPerMWh: number;
}

export const PROPOSED_CAMPUS_SCREENING_ASSUMPTIONS: Readonly<CampusScreeningAssumptions> = Object.freeze({
  incrementalReservedLandFraction: 0.30,
  peakFacilityToITRatio: 1.50,
  roundDownITMW: 5,
  normalPeakNonEvaporativeWithdrawalBudgetGpd: 30_000,
  protectedWaterBudgetGpd: 20_000,
  assumedDesignDryBulbC: 40,
  assumedGasHeatRateHHVMMBtuPerMWh: 10,
});

export interface CampusScreeningCase {
  referenceId: string;
  transferAreaAcres: number;
  referenceCriticalITMWPerCampusAcre: number;
  unroundedCaseCeilingITMW: number;
  roundedCaseCeilingITMW: number;
  correspondingPeakFacilityMW: number;
  gasRequiredAtPeakHHVMMBtuPerHour: number;
}

/** Safe display contract; private source witnesses stay in the frozen envelope.
 * A band brackets two analogy-case ceilings; its low endpoint is NOT a minimum
 * feasible/economic deployment, nor a claim that every case supports its high end.
 */
export interface CampusResourceScreeningResult {
  schemaVersion: 1;
  methodVersion: typeof CAMPUS_RESOURCE_SCREENING_VERSION;
  catalogVersion: typeof CAMPUS_REFERENCE_CATALOG_VERSION;
  status: 'conditional-comparison' | 'usable-land-unresolved' | 'no-area-under-assumptions' | 'water-budget-does-not-fit';
  decisionAuthority: 'none';
  qualification: 'fictional-illustrative' | 'source-stated-planning-analogy';
  /** Exact safe facts/assumed/fictional/conflict flags, not private witnesses. */
  sourceResources: SiteResourceView | null;
  assumptions: CampusScreeningAssumptions;
  cases: CampusScreeningCase[];
  /** The band is a recommendation to investigate these endpoints, not supply. */
  recommendedScreeningComparisonRangeITMW: { low: number; high: number } | null;
  correspondingAssumedPeakFacilityRangeMW: { low: number; high: number } | null;
  waterAllocationGpd: number | null;
  waterBudgetStatus: 'fits-proposed-budget-only' | 'unverified' | 'does-not-fit';
  deliverableGasCapacity: null;
  firmElectricalCapacity: null;
  engineeringVerdict: 'indeterminate';
  conditions: string[];
  sourceUrls: string[];
}

export const campusScreeningAssumptionsSchema = z.object({
  incrementalReservedLandFraction: z.number().finite().min(0).max(1),
  peakFacilityToITRatio: z.number().finite().min(1),
  roundDownITMW: z.number().finite().positive(),
  normalPeakNonEvaporativeWithdrawalBudgetGpd: z.number().finite().nonnegative(),
  protectedWaterBudgetGpd: z.number().finite().nonnegative(),
  assumedDesignDryBulbC: z.number().finite().min(-90).max(70),
  assumedGasHeatRateHHVMMBtuPerMWh: z.number().finite().positive(),
}).strict();

export function calculateCampusResourceScreening(
  sourceValidatedResources: SiteResourceView | null,
  assumptions: Readonly<CampusScreeningAssumptions> = PROPOSED_CAMPUS_SCREENING_ASSUMPTIONS,
): CampusResourceScreeningResult {
  const validatedAssumptions = campusScreeningAssumptionsSchema.safeParse(assumptions);
  if (!validatedAssumptions.success) throw new Error('Invalid explicit campus screening assumptions');
  assumptions = validatedAssumptions.data;
  const view = sourceValidatedResources == null ? null : siteResourceViewSchema.parse(sourceValidatedResources);
  const land = view?.facts.find(fact => fact.key === 'netUsableAcres');
  const water = view?.facts.find(fact => fact.key === 'siteWaterAllocationMgd');
  const service = view?.facts.find(fact => fact.key === 'waterServiceDate');
  const allocation = water?.key === 'siteWaterAllocationMgd' ? water.value * 1_000_000 : null;
  const requiredBudget = assumptions.normalPeakNonEvaporativeWithdrawalBudgetGpd + assumptions.protectedWaterBudgetGpd;
  if ((allocation != null && !Number.isFinite(allocation)) || !Number.isFinite(requiredBudget)) {
    throw new Error('Campus water budget arithmetic overflow.');
  }
  const waterBudgetStatus = allocation == null ? 'unverified'
    : allocation < requiredBudget ? 'does-not-fit' : 'fits-proposed-budget-only';
  const conditions = [
    'Reference campus acreage is a whole-site denominator. Transferring it to this site\'s source-stated usable acres is an explicit analogy, not a geometric fit or universal density.',
    `${assumptions.incrementalReservedLandFraction * 100}% of usable area is held back for incremental BTM infrastructure and layout uncertainty; this is an assumed allowance, not a proven equipment footprint.`,
    'The range brackets conservative and compact reference-case ceilings. Its lower endpoint is not a minimum phase or economic scale; the compact endpoint depends on the compact layout assumption.',
    `Peak facility demand is assumed to be ${assumptions.peakFacilityToITRatio} times IT load. This is not annual PUE; no annual energy or WUE result is converted into a peak-service guarantee.`,
    `Cooling uses a closed-loop, non-evaporative heat-rejection hypothesis at an assumed ${assumptions.assumedDesignDryBulbC}°C design dry bulb. Air-cooled chiller/heat-exchanger performance, rack compatibility and redundancy remain unverified.`,
    `Routine peak non-evaporative withdrawal must stay within the assumed ${assumptions.normalPeakNonEvaporativeWithdrawalBudgetGpd} gpd budget, with ${assumptions.protectedWaterBudgetGpd} gpd protected. These are budget conditions, not modeled demand; fire water, initial fill, maintenance/refill peaks and service pressure/delivery require separate checks.`,
    allocation == null ? 'Site-specific water allocation is unknown; the proposed routine budget is not a supply commitment.'
      : 'A source-stated daily water allocation is not proof of instantaneous peak delivery, pressure, water chemistry, permit authority or current service.',
    service?.key === 'waterServiceDate' ? `Source-stated water service date: ${service.value}${service.assumed ? ' (assumed)' : ''}; service is not presumed active.` : 'Water service date is unknown.',
    `Gas-led continuous BTM, if elected, requires verified site-net duty and HHV fuel nomination at the assumed ${assumptions.assumedGasHeatRateHHVMMBtuPerMWh} MMBtu/MWh heat rate. Required fuel is not available fuel; deliverable gas capacity stays unknown.`,
    'BTM supply can be investigated without a grid utility letter. Grid import/export, supply adequacy, N+1/UPS/storage, fuel security, air/noise permits and engineering remain unresolved; standby/renewable nameplate is not continuous firm supply.',
    'This advisory changes no Intent, source grade, utility CFS, scenario selection, report hold or decision readiness.',
  ];
  const result: CampusResourceScreeningResult = {
    schemaVersion: 1, methodVersion: CAMPUS_RESOURCE_SCREENING_VERSION,
    catalogVersion: CAMPUS_REFERENCE_CATALOG_VERSION,
    status: 'usable-land-unresolved', decisionAuthority: 'none',
    qualification: view?.facts.some(fact => fact.fictional) ? 'fictional-illustrative' : 'source-stated-planning-analogy',
    sourceResources: view,
    assumptions: { ...assumptions }, cases: [], recommendedScreeningComparisonRangeITMW: null,
    correspondingAssumedPeakFacilityRangeMW: null, waterAllocationGpd: allocation,
    waterBudgetStatus, deliverableGasCapacity: null, firmElectricalCapacity: null,
    engineeringVerdict: 'indeterminate', conditions,
    sourceUrls: CAMPUS_REFERENCE_CASES.map(reference => reference.sourceUrl),
  };
  if (land?.key !== 'netUsableAcres') return result;
  const transferAreaAcres = land.value * (1 - assumptions.incrementalReservedLandFraction);
  result.cases = CAMPUS_REFERENCE_CASES.map(reference => {
    const density = reference.criticalITMW / reference.campusAcres;
    const ceiling = transferAreaAcres * density;
    const rounded = Math.floor(ceiling / assumptions.roundDownITMW) * assumptions.roundDownITMW;
    const peak = rounded * assumptions.peakFacilityToITRatio;
    const fuel = peak * assumptions.assumedGasHeatRateHHVMMBtuPerMWh;
    if (![transferAreaAcres, density, ceiling, rounded, peak, fuel].every(Number.isFinite)) {
      throw new Error('Campus analogy arithmetic overflow.');
    }
    return { referenceId: reference.id, transferAreaAcres,
      referenceCriticalITMWPerCampusAcre: density, unroundedCaseCeilingITMW: ceiling,
      roundedCaseCeilingITMW: rounded, correspondingPeakFacilityMW: peak,
      gasRequiredAtPeakHHVMMBtuPerHour: fuel };
  });
  if (result.cases.some(row => row.roundedCaseCeilingITMW <= 0)) {
    result.status = 'no-area-under-assumptions';
    return result;
  }
  if (waterBudgetStatus === 'does-not-fit') {
    result.status = 'water-budget-does-not-fit';
    return result;
  }
  result.status = 'conditional-comparison';
  result.recommendedScreeningComparisonRangeITMW = {
    low: Math.min(...result.cases.map(row => row.roundedCaseCeilingITMW)),
    high: Math.max(...result.cases.map(row => row.roundedCaseCeilingITMW)),
  };
  result.correspondingAssumedPeakFacilityRangeMW = {
    low: Math.min(...result.cases.map(row => row.correspondingPeakFacilityMW)),
    high: Math.max(...result.cases.map(row => row.correspondingPeakFacilityMW)),
  };
  return result;
}
